'use client';

import { useMemo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { red } from '@mui/material/colors';

import { ComponentErrorBoundary, SearchResultItemSkeleton, TabSwitcher } from '@/components';
import { useT } from '@/hooks';
import { useSearchDropdown } from '@/hooks';
import { SearchDropdownContent } from '../Dropdown/SearchDropdownContent';
import { SearchMarketCard } from './SearchMarketCard';
import { SearchPageHeader } from './SearchPageHeader';
import { SearchProfileCard } from './SearchProfileCard';

import type { BrowseFilter } from '@/constants';

interface MainContentProps {
  searchQuery: string;
  status: string;
  searchInputRef: React.RefObject<HTMLDivElement>;
  handleBrowseClick: (filter: BrowseFilter) => void;
  handleStatusChange: (status: string) => void;
  handleClearFilters: () => void;
}

interface SearchContentRendererProps {
  isSearchLoading: boolean;
  hasAnyResults: boolean;
  searchQuery: string;
  searchResults: any;
  recentSearches: any[];
  handleBrowseClick: (filter: BrowseFilter) => void;
  handleResultClick: (event: any) => void;
  removeFromRecent: (id: string, e: React.MouseEvent) => void;
  handleProfileClick: (profile: { proxyWallet: string }) => void;
  handleSeeAllClick: () => void;
  clearAllRecent: () => void;
  searchedList: React.ReactNode;
  activeTab: 'market' | 'profile';
  setActiveTab: (tab: 'market' | 'profile') => void;
}

function SearchContentRenderer({
  isSearchLoading,
  hasAnyResults,
  searchQuery,
  searchResults,
  recentSearches,
  handleBrowseClick,
  handleResultClick,
  removeFromRecent,
  handleProfileClick,
  handleSeeAllClick,
  clearAllRecent,
  activeTab,
  setActiveTab,
  searchedList,
}: SearchContentRendererProps) {
  const { t } = useT();

  if (isSearchLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <SearchResultItemSkeleton key={i} />
        ))}
      </Box>
    );
  }

  if (hasAnyResults) {
    return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>{searchedList}</Box>;
  }

  if (searchQuery) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          {t('search.noResultsFound', { query: searchQuery })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('search.tryAdjusting')}
        </Typography>
      </Box>
    );
  }

  return (
    <SearchDropdownContent
      showResults={false}
      isLoading={isSearchLoading}
      hasAnyResults={false}
      searchQuery={searchQuery}
      recentSearches={recentSearches}
      searchResults={searchResults}
      handleBrowseClick={handleBrowseClick}
      handleResultClick={handleResultClick}
      removeFromRecent={removeFromRecent}
      handleProfileClick={handleProfileClick}
      handleSeeAllClick={handleSeeAllClick}
      clearAllRecent={clearAllRecent}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      containerStyle={{ py: { xs: 1, sm: 1.5 } }}
    />
  );
}

export function SearchPageResults({
  searchQuery,
  status,
  searchInputRef,
  handleBrowseClick,
  handleStatusChange,
}: MainContentProps) {
  const { t } = useT();
  const [page, setPage] = useState(1);
  const [prevSearchQuery, setPrevSearchQuery] = useState(searchQuery);
  const [prevStatus, setPrevStatus] = useState(status);

  if (searchQuery !== prevSearchQuery || status !== prevStatus) {
    setPrevSearchQuery(searchQuery);
    setPrevStatus(status);
    setPage(1);
  }
  const {
    state,
    handlers,
    ref: { dropdownRef },
  } = useSearchDropdown({
    isOpen: searchQuery.length > 0,
    onClose: () => {},
    anchorRef: searchInputRef as React.RefObject<HTMLElement | null>,
    searchQuery,
    status,
    page,
  });

  const {
    recentSearches,
    searchResults,
    isLoading: isSearchLoading,
    hasAnyResults,
    activeTab,
    hasMore,
    totalResults,
  } = state;

  const {
    handleResultClick,
    handleProfileClick,
    handleSeeAllClick,
    removeFromRecent,
    clearAllRecent,
    setActiveTab,
  } = handlers;

  const searchedList = useMemo(() => {
    if (activeTab === 'market') {
      return (searchResults?.events || []).map((event: any) => (
        <SearchMarketCard key={event.id} event={event} />
      ));
    }
    if (activeTab === 'profile') {
      return (searchResults?.profiles || []).map((profile: any) => (
        <SearchProfileCard
          key={profile.proxyWallet}
          profile={profile}
          onClick={handleProfileClick}
        />
      ));
    }
    return null;
  }, [
    searchResults?.events,
    searchResults?.profiles,
    activeTab,
    handleResultClick,
    handleProfileClick,
  ]);

  return (
    <ComponentErrorBoundary componentName="SearchPageResults">
      <Box
        sx={{
          width: { xs: '100%', lg: '70%' },
          pr: { lg: 2 },
        }}
      >
        {searchQuery && (
          <SearchPageHeader
            count={totalResults || searchResults?.events?.length || 0}
            query={searchQuery}
          />
        )}

        {searchQuery && hasAnyResults && (
          <TabSwitcher
            activeTab={activeTab}
            onTabChange={(tab) => setActiveTab(tab as 'market' | 'profile')}
            options={[
              { value: 'market', label: t('search.markets') },
              { value: 'profile', label: t('search.profiles') },
            ]}
            containerSx={{ px: 0, mb: 2 }}
          />
        )}

        <Box
          ref={dropdownRef}
          sx={{
            height: { xs: 'auto', lg: '40rem' },
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: 0.5,
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'divider',
              borderRadius: 2,
              '&:hover': {
                bgcolor: 'text.disabled',
              },
            },
          }}
        >
          <SearchContentRenderer
            isSearchLoading={isSearchLoading}
            hasAnyResults={hasAnyResults}
            searchQuery={searchQuery}
            searchResults={searchResults}
            recentSearches={recentSearches}
            handleBrowseClick={handleBrowseClick}
            handleResultClick={handleResultClick}
            removeFromRecent={removeFromRecent}
            handleProfileClick={handleProfileClick}
            handleSeeAllClick={handleSeeAllClick}
            clearAllRecent={clearAllRecent}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            searchedList={searchedList}
          />
          {hasAnyResults && searchQuery && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 2,
                mt: 4,
                mb: 4,
              }}
            >
              <Button
                variant="outlined"
                disabled={page === 1 || isSearchLoading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                {t('search.previous')}
              </Button>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ minWidth: '60px', textAlign: 'center' }}
              >
                {t('search.page', { page })}
              </Typography>
              <Button
                variant="outlined"
                disabled={!hasMore || isSearchLoading}
                onClick={() => setPage((p) => p + 1)}
              >
                {t('search.next')}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </ComponentErrorBoundary>
  );
}
